import React, { useEffect, useState } from 'react';
import { Form, message, ConfigProvider, Select } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import {
  fetchAssignmentDeviceTypes,
  fetchAssignmentMerchants,
  fetchAssignmentStores,
  createAssignmentDevice,
  updateAssignmentDevice,
  clearAssignmentStores,
} from '../../redux/DeviceAssignment';
import { DeviceModalProps, DeviceFormData } from './types';
import {
  LABELS,
  VALIDATION_MESSAGES,
  PLACEHOLDERS,
  MESSAGES,
} from './constants';
import {
  StyledModal,
  ModalContainerGlobalStyle,
  StyledFormItem,
  StyledFormLabel,
  StyledInput,
  StyledSelect,
  SaveButton,
  CancelButton,
} from './styles';

const DeviceModal: React.FC<DeviceModalProps> = ({
  open,
  initialDevice,
  onSave,
  onCancel,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const { deviceTypes, merchants, stores, loading } = useSelector(
    (state: RootState) => state.deviceAssignment
  );
  const [form] = Form.useForm();
  const [merchantId, setMerchantId] = useState<string>('');
  const [isUnsavedChanges, setIsUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const isEditMode = !!initialDevice;

  useEffect(() => {
    dispatch(fetchAssignmentDeviceTypes());
    dispatch(fetchAssignmentMerchants());
  }, [dispatch]);

  useEffect(() => {
    if (merchantId) {
      dispatch(fetchAssignmentStores(merchantId));
    } else {
      dispatch(clearAssignmentStores());
    }
  }, [merchantId, dispatch]);

  useEffect(() => {
    if (open) {
      if (initialDevice) {
        const merchantValue = typeof initialDevice.merchant === 'object'
          ? initialDevice.merchant.id
          : initialDevice.merchant;
        form.setFieldsValue({
          name: initialDevice.name,
          identifier: initialDevice.identifier,
          type: typeof initialDevice.type === 'object' ? initialDevice.type.id : initialDevice.type,
          merchant: merchantValue,
          store: typeof initialDevice.store === 'object' ? initialDevice.store.id : initialDevice.store,
        });
        if (merchantValue) {
          setMerchantId(merchantValue);
        }
      } else {
        form.resetFields();
        setMerchantId('');
        setIsUnsavedChanges(false);
      }
    }
  }, [initialDevice, form, open]);

  const handleMerchantChange = (value: string) => {
    setMerchantId(value);
    form.setFieldsValue({ store: undefined });
    setIsUnsavedChanges(true);
  };

  const handleFormChange = () => {
    setIsUnsavedChanges(true);
  };

  const resetForm = () => {
    form.resetFields();
    setMerchantId('');
    setIsUnsavedChanges(false);
  };

  const handleCancel = () => {
    if (isUnsavedChanges) {
      setShowConfirmModal(true);
      return;
    }
    resetForm();
    onCancel();
  };

  const confirmCancel = () => {
    setShowConfirmModal(false);
    resetForm();
    onCancel();
  };

  const handleSubmit = async (values: DeviceFormData) => {
    try {
      const deviceData = {
        name: values.name,
        identifier: values.identifier,
        type: values.type,
        merchant: values.merchant,
        store: values.store,
      };

      if (isEditMode && initialDevice) {
        await dispatch(
          updateAssignmentDevice({ id: initialDevice.id, data: deviceData })
        ).unwrap();
        message.success(MESSAGES.DEVICE_UPDATED);
      } else {
        await dispatch(createAssignmentDevice(deviceData)).unwrap();
        message.success(MESSAGES.DEVICE_CREATED);
      }

      resetForm();
      onSave();
    } catch (error: any) {
      message.error(
        error || (isEditMode ? MESSAGES.ERROR_UPDATE : MESSAGES.ERROR_CREATE)
      );
    }
  };

  const showStoreField =
    merchantId ||
    (initialDevice &&
      typeof initialDevice.store === 'object' &&
      initialDevice.store?.id);

  return (
    <>
      <ModalContainerGlobalStyle $isDark={isDarkMode} />
      <StyledModal
        $isDark={isDarkMode}
        title={
          isEditMode
            ? `${LABELS.EDIT_DEVICE} - ${initialDevice?.name}`
            : LABELS.ADD_DEVICE
        }
        open={open && !showConfirmModal}
        onCancel={handleCancel}
        maskClosable={!isUnsavedChanges}
        keyboard={!isUnsavedChanges}
        closable
        footer={[
          <CancelButton
            key="cancel"
            onClick={handleCancel}
            disabled={loading.create || loading.update}
          >
            {LABELS.CANCEL}
          </CancelButton>,
          <SaveButton
            key="submit"
            type="primary"
            icon={<FontAwesomeIcon icon={faSave} />}
            loading={loading.create || loading.update}
            onClick={() => form.submit()}
          >
            {loading.create || loading.update ? LABELS.SAVING : LABELS.SAVE}
          </SaveButton>,
        ]}
        width={600}
        destroyOnClose
        afterClose={resetForm}
      >
        <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={handleFormChange}
            initialValues={{
              name: '',
              identifier: '',
              type: undefined,
              merchant: undefined,
              store: undefined,
            }}
          >
            <StyledFormItem>
              <Form.Item
                name="name"
                label={
                  <StyledFormLabel $isDark={isDarkMode}>
                    {LABELS.NAME}
                  </StyledFormLabel>
                }
                rules={[
                  { required: true, message: VALIDATION_MESSAGES.NAME_REQUIRED },
                ]}
              >
                <StyledInput
                  $isDark={isDarkMode}
                  placeholder={PLACEHOLDERS.DEVICE_NAME}
                />
              </Form.Item>
            </StyledFormItem>

            <StyledFormItem>
              <Form.Item
                name="identifier"
                label={
                  <StyledFormLabel $isDark={isDarkMode}>
                    {LABELS.IDENTIFIER}
                  </StyledFormLabel>
                }
                rules={[
                  {
                    required: true,
                    message: VALIDATION_MESSAGES.IDENTIFIER_REQUIRED,
                  },
                ]}
              >
                <StyledInput
                  $isDark={isDarkMode}
                  placeholder={PLACEHOLDERS.DEVICE_IDENTIFIER}
                />
              </Form.Item>
            </StyledFormItem>

            <StyledFormItem>
              <Form.Item
                name="type"
                label={
                  <StyledFormLabel $isDark={isDarkMode}>
                    {LABELS.DEVICE_TYPE}
                  </StyledFormLabel>
                }
                rules={[
                  { required: true, message: VALIDATION_MESSAGES.TYPE_REQUIRED },
                ]}
              >
                <StyledSelect
                  $isDark={isDarkMode}
                  placeholder={PLACEHOLDERS.SELECT_TYPE}
                >
                  {deviceTypes.map((type) => (
                    <Select.Option key={type.id} value={type.id}>
                      {type.name}
                    </Select.Option>
                  ))}
                </StyledSelect>
              </Form.Item>
            </StyledFormItem>

            <StyledFormItem>
              <Form.Item
                name="merchant"
                label={
                  <StyledFormLabel $isDark={isDarkMode}>
                    {LABELS.MERCHANT}
                  </StyledFormLabel>
                }
              >
                <StyledSelect
                  $isDark={isDarkMode}
                  placeholder={PLACEHOLDERS.SELECT_MERCHANT}
                  onChange={handleMerchantChange}
                >
                  {merchants.map((merchant) => (
                    <Select.Option key={merchant.id} value={merchant.id}>
                      {merchant.name}
                    </Select.Option>
                  ))}
                </StyledSelect>
              </Form.Item>
            </StyledFormItem>

            {showStoreField && (
              <StyledFormItem>
                <Form.Item
                  name="store"
                  label={
                    <StyledFormLabel $isDark={isDarkMode}>
                      {LABELS.STORE}
                    </StyledFormLabel>
                  }
                >
                  <StyledSelect
                    $isDark={isDarkMode}
                    placeholder={PLACEHOLDERS.SELECT_STORE}
                  >
                    {stores.map((store) => (
                      <Select.Option key={store.id} value={store.id}>
                        {store.name}
                      </Select.Option>
                    ))}
                  </StyledSelect>
                </Form.Item>
              </StyledFormItem>
            )}
          </Form>
        </ConfigProvider>
      </StyledModal>

      <StyledModal
        $isDark={isDarkMode}
        title={LABELS.UNSAVED_CHANGES}
        open={showConfirmModal}
        onOk={confirmCancel}
        onCancel={() => setShowConfirmModal(false)}
        okText={LABELS.YES}
        cancelText={LABELS.NO}
        maskClosable={false}
      >
        <p>{MESSAGES.UNSAVED_CHANGES}</p>
      </StyledModal>
    </>
  );
};

export default DeviceModal;
